import { BatteryData } from '../../api/service/BatteryInterfaces';
import React, { useState } from 'react';
import styled from 'styled-components';
import leaf from '../../assets/images/leaf.svg';
import { IoCloseOutline } from 'react-icons/io5';
import BuyerInquiryService from '../../api/service/buyer/BuyerInquiryService';
import { formatPhoneNumber } from '../../utils/formatPhoneNumber';

interface InquiryPopupProps {
  onClose: () => void;
  item?: BatteryData | null;
}

const PopupContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const PopupContent = styled.div`
  position: relative;
  background-color: ${(props) => props.theme.colors.bgColor};
  border-radius: 10px;
  width: 800px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  @media (max-width: 968px) {
    width: 95%;
  }
`;
const IoCloseButton = styled(IoCloseOutline)`
  position: absolute;
  right: 30px;
  font-size: 24px;
  @media (max-width: 968px) {
    font-size: 20px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 10px;
  img {
    width: 24px;
  }
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;

  th,
  td {
    font-size: 16px;
    border: 1px solid #ddd;
    padding: 10px;
    text-align: left;
    vertical-align: middle;
  }

  th {
    background-color: ${(props) => props.theme.colors.subColor};
    color: ${(props) => props.theme.colors.whiteColor};
    font-weight: 500;
  }
  td {
    background-color: ${(props) => props.theme.colors.whiteColor};
  }

  @media (max-width: 968px) {
    th,
    td {
      font-size: 12px;
    }
  }
`;

const FlexContainer = styled.div`
  display: flex;
  gap: 10px;
  input {
    border: 1px solid #ddd;
    padding: 5px 10px;
  }

  @media (max-width: 968px) {
    flex-direction: column;
    input {
      font-size: 12px;
    }
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 5px 10px;
  height: 200px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const SaveButton = styled.button`
  width: 80px;
  padding: 10px 15px;
  border: none;
  border-radius: 3px;
  font-size: 14px;
  color: white;
  cursor: pointer;
  background-color: ${(props) => props.theme.colors.subColor};

  &:hover {
    background-color: ${(props) => props.theme.colors.subHoverColor};
  }

  @media (max-width: 968px) {
    padding: 5px 10px;
  }
`;
const CloseButton = styled.button`
  width: 80px;
  padding: 10px 15px;
  border: none;
  border-radius: 3px;
  font-size: 14px;
  color: white;
  cursor: pointer;
  background-color: ${(props) => props.theme.colors.redColor};

  &:hover {
    background-color: ${(props) => props.theme.colors.redHoverColor};
  }

  @media (max-width: 968px) {
    padding: 5px 10px;
  }
`;

const InquiryPopup: React.FC<InquiryPopupProps> = ({ onClose, item }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // 전화번호 필드는 포맷팅 적용
    if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value);
      setFormData((prev) => ({ ...prev, phone: formattedPhone }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };
  const handleSubmit = async () => {
    if (!formData.name || !formData.phone || !formData.message) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    // API 호출
    try {
      const payload = {
        batteryId: item?.batteryId || '',
        buyerName: formData.name,
        buyerPhoneNumber: formData.phone,
        content: formData.message,
      };

      const response = await BuyerInquiryService.postInquiryToSeller(payload);
      console.log('문의 등록 성공:', response);
      alert('문의가 성공적으로 전송되었습니다!');
      onClose(); // 팝업 닫기
    } catch (error) {
      console.error('문의 등록 실패:', error);
      alert(error);
    }
  };

  return (
    <PopupContainer>
      <PopupContent>
        <IoCloseButton onClick={onClose} />
        <TitleContainer>
          <img src={leaf} alt="leaf" />
          <Title>문의하기</Title>
        </TitleContainer>
        <Table>
          <tbody>
            <tr>
              <th>구매자 정보</th>
              <td>
                <FlexContainer>
                  <input
                    type="text"
                    name="name"
                    placeholder="이름"
                    value={formData.name}
                    onChange={handleChange}
                    style={{ width: '150px' }}
                  />
                  <input
                    type="text"
                    name="phone"
                    placeholder="휴대전화번호"
                    value={formData.phone}
                    onChange={handleChange}
                    style={{ width: '100%' }}
                  />
                </FlexContainer>
              </td>
            </tr>
            <tr>
              <th>차종 / 고유번호</th>
              <td>
                <FlexContainer>
                  <input
                    type="text"
                    value={item?.model || ''}
                    readOnly
                    style={{ width: '150px' }}
                  />
                  <input
                    type="text"
                    value={item?.manufacturer || ''}
                    readOnly
                    style={{ width: '100px' }}
                  />
                  <input
                    type="text"
                    value={item?.identifier || ''}
                    readOnly
                    style={{ width: '100%' }}
                  />
                </FlexContainer>
              </td>
            </tr>
            <tr>
              <th>문의내용</th>
              <td>
                <TextArea
                  name="message"
                  placeholder="문의 내용을 입력해주세요"
                  value={formData.message}
                  onChange={handleChange}
                />
              </td>
            </tr>
          </tbody>
        </Table>
        <ButtonGroup>
          <SaveButton onClick={handleSubmit}>문의하기</SaveButton>
          <CloseButton onClick={onClose}>취소</CloseButton>
        </ButtonGroup>
      </PopupContent>
    </PopupContainer>
  );
};

export default InquiryPopup;
